import { useEffect, useState } from "react";
import { IS_NATIVE_APP, APP_TARGET } from "../../config/appTarget.js";
import { API_BASE_URL } from "../../lib/apiClient.js";
import { initClientMeta } from "../../lib/clientMeta.js";
import { PLAY_STORE_AVAILABLE } from "../../lib/appStoreLinks.js";
import {
  computeUpdateStatus,
  IDLE_UPDATE_STATUS,
  type AppUpdateStatus,
} from "./updateStatus.js";

/**
 * Native-only update check. On launch it fetches `GET /api/app-config` once and
 * compares this binary's build number against two platform floors:
 *
 *   - `minSupported` -> if the binary is below it, `forceUpdate` is true and the
 *     gate shows a blocking "please update" screen (hard cutover).
 *   - `latest` -> if a newer build is live in the store, `updateAvailable` is
 *     true and the gate surfaces a dismissible "nova versao" prompt (soft nudge).
 *
 * `latest` is env-overridable on the backend (LATEST_IOS_BUILD /
 * LATEST_ANDROID_BUILD) so the prompt only fires once the new build is actually
 * downloadable, not merely because the backend was redeployed.
 *
 * Fails open: any network/parse error or a web build leaves the app usable and
 * un-prompted.
 */
export type { AppUpdateStatus };

interface AppConfigResponse {
  success?: boolean;
  minSupported?: { ios?: number; android?: number };
  latest?: { ios?: number; android?: number };
}

export function useAppUpdate(): AppUpdateStatus {
  const [status, setStatus] = useState<AppUpdateStatus>(IDLE_UPDATE_STATUS);

  useEffect(() => {
    if (!IS_NATIVE_APP) return;

    let cancelled = false;
    (async () => {
      try {
        const meta = await initClientMeta();
        if (meta.build == null) return; // no build number → can't gate, stay usable

        const res = await fetch(`${API_BASE_URL}/api/app-config`);
        if (!res.ok) return;
        const data = (await res.json()) as AppConfigResponse;

        const next = computeUpdateStatus({
          platform: APP_TARGET,
          build: meta.build,
          minSupported:
            APP_TARGET === "ios"
              ? data.minSupported?.ios
              : data.minSupported?.android,
          latest:
            APP_TARGET === "ios" ? data.latest?.ios : data.latest?.android,
          playStoreAvailable: PLAY_STORE_AVAILABLE,
        });

        if (!cancelled) setStatus(next);
      } catch {
        // Fail open — never block or nag the app because the config call failed.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return status;
}
